/**
 * Live Monitor Page
 * Real-time driver tracking and route progress
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Select } from '../components/ui';
import {
  LiveDriverMap,
  DriverStatusList,
  LiveStatsPanel,
  AlertsPanel,
} from '../components/monitor';
import type { Alert } from '../components/monitor';
import {
  useLiveDriverPositions,
  useRouteRuns,
  useDriverTrack,
  useDriverRoutesToday,
} from '../hooks/useApi';
import { RefreshCw, Loader2 } from 'lucide-react';
import { getBaghdadToday } from '../utils/date';
import { CITIES } from '../utils/cities';

// Auto-refresh interval (30 seconds)
const REFRESH_INTERVAL = 30000;

export default function LiveMonitor() {
  const [cityId, setCityId] = useState(1);
  const [selectedDriverUid, setSelectedDriverUid] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  // Clock for "active driver" age checks; ticks every REFRESH_INTERVAL so stats stay current
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Get today's date
  const today = getBaghdadToday();

  // Queries
  const {
    data: driversData,
    isLoading: isLoadingDrivers,
    refetch: refetchDrivers,
  } = useLiveDriverPositions(cityId);

  const {
    data: routeRunsData,
    isLoading: isLoadingRoutes,
    refetch: refetchRoutes,
  } = useRouteRuns(cityId, today);

  // Selected driver's path and stops today
  const { data: track, isError: trackError, refetch: refetchTrack } = useDriverTrack(selectedDriverUid || undefined);
  const { data: driverRoutes, isError: routesError, refetch: refetchDriverRoutes } = useDriverRoutesToday(selectedDriverUid || undefined);

  // Get drivers array
  const drivers = driversData || [];

  // Get today's routes
  const todayRouteRun = routeRunsData?.find((run) => run.date === today);

  // Calculate stats
  const stats = {
    totalDrivers: drivers.length,
    activeDrivers: drivers.filter((d) => {
      const ageMinutes = (now - new Date(d.recorded_at).getTime()) / 60000;
      return ageMinutes < 15;
    }).length,
    totalRoutes: todayRouteRun?.route_count || 0,
    completedStops: 0, // Would come from route data
    pendingStops: todayRouteRun?.task_count || 0,
    delayedStops: 0, // Would come from route data
  };

  // No alert source exists yet; stays empty rather than showing invented alerts.
  const [alerts, setAlerts] = useState<Alert[]>([]);

  // Refresh data
  const handleRefresh = useCallback(() => {
    refetchDrivers();
    refetchRoutes();
    // refetch() ignores `enabled`, so only with a driver picked
    if (selectedDriverUid) {
      refetchTrack();
      refetchDriverRoutes();
    }
    setLastRefresh(new Date());
  }, [refetchDrivers, refetchRoutes, refetchTrack, refetchDriverRoutes, selectedDriverUid]);

  // Auto-refresh effect
  useEffect(() => {
    if (!isAutoRefresh) return;

    const interval = setInterval(() => {
      handleRefresh();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAutoRefresh, handleRefresh]);

  // Handle alert dismiss
  const handleDismissAlert = useCallback((alertId: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }, []);

  // Handle alert mark read
  const handleMarkRead = useCallback((alertId: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, isRead: true } : a))
    );
  }, []);

  const isLoading = isLoadingDrivers || isLoadingRoutes;

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Live Monitor</h1>
          <p className="text-gray-500 text-sm">
            Real-time driver tracking and route progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-40">
            <Select
              options={CITIES}
              value={cityId}
              onChange={(e) => {
                setCityId(parseInt(e.target.value, 10));
                setSelectedDriverUid(null);
              }}
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Last updated: {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' })}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              leftIcon={
                isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )
              }
            >
              Refresh
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isAutoRefresh}
              onChange={(e) => setIsAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            Auto-refresh
          </label>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <LiveStatsPanel stats={stats} isLoading={isLoading} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Driver List */}
        {/* Wider only on large screens: on a 1366px laptop with the sidebar open, w-96 left the map ~400px */}
        <div className="w-80 2xl:w-96 shrink-0 border-r border-gray-200 bg-white overflow-y-auto p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            Drivers on map ({drivers.length})
          </h2>
          <DriverStatusList
            drivers={drivers}
            selectedDriverUid={selectedDriverUid || undefined}
            onSelectDriver={setSelectedDriverUid}
            isLoading={isLoadingDrivers}
          />
        </div>

        {/* Center - Map */}
        <div className="flex-1 relative">
          <LiveDriverMap
            drivers={drivers}
            selectedDriverUid={selectedDriverUid || undefined}
            onDriverSelect={setSelectedDriverUid}
            onClearSelection={() => setSelectedDriverUid(null)}
            trail={track}
            routes={driverRoutes}
            trailError={trackError}
            routesError={routesError}
            className="absolute inset-0"
          />
        </div>

        {/* Right Panel - Alerts & Routes */}
        <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto">
          {/* Alerts */}
          <div className="p-4 border-b border-gray-200">
            <AlertsPanel
              alerts={alerts}
              onDismiss={handleDismissAlert}
              onMarkRead={handleMarkRead}
              maxHeight="200px"
            />
          </div>

          {/* Active Routes */}
          <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              Today's Routes
            </h2>
            {isLoadingRoutes ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : todayRouteRun ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                <p>{todayRouteRun.route_count} routes</p>
                <p>{todayRouteRun.task_count} tasks</p>
                <p className="text-xs mt-1">
                  Status: {todayRouteRun.status}
                </p>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                No routes for today
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
